import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction
} from 'react'
import type { ChatpbChatMessage } from '@itsrever/rever-api'
import type {
  ChatFeedbackRating,
  ChatFollowupKind,
  ChatScreenRegistration
} from './types'

interface ChatContextValue {
  activeContext: ChatScreenRegistration | null
  registerContext: (id: string, registration: ChatScreenRegistration) => void
  unregisterContext: (id: string) => void
  // The conversation lives here, not in the panel, so closing and reopening the
  // panel (which unmounts it) keeps the messages for the whole session.
  messages: ChatpbChatMessage[]
  setMessages: Dispatch<SetStateAction<ChatpbChatMessage[]>>
  rulesUpdatedIndexes: Set<number>
  setRulesUpdatedIndexes: Dispatch<SetStateAction<Set<number>>>
  // Rating per assistant turn, keyed by its index in `messages`. Lives here for
  // the same reason as the thread: reopening the panel must not offer to rate
  // a reply the user already rated.
  feedbackByIndex: Map<number, ChatFeedbackRating>
  setFeedbackByIndex: Dispatch<SetStateAction<Map<number, ChatFeedbackRating>>>
  // Only the latest reply's follow-ups, so older chips never linger in the thread.
  followups: ChatFollowupKind[]
  setFollowups: Dispatch<SetStateAction<ChatFollowupKind[]>>
  // Panel open state lives here so any screen can open the assistant (e.g. an
  // "Edit with AI" button), not just the floating launcher.
  isOpen: boolean
  openChat: () => void
  closeChat: () => void
  // Whether the assistant is enabled for this user/ecommerce. Published by the
  // mounted chat so screens can show or hide their own chat entry points.
  isChatAvailable: boolean
  setChatAvailable: Dispatch<SetStateAction<boolean>>
}

const notWrapped = () => {
  throw new Error('Component is not wrapped with a ChatProvider.')
}

const ChatContext = createContext<ChatContextValue>({
  activeContext: null,
  registerContext: notWrapped,
  unregisterContext: notWrapped,
  messages: [],
  setMessages: notWrapped,
  rulesUpdatedIndexes: new Set(),
  setRulesUpdatedIndexes: notWrapped,
  feedbackByIndex: new Map(),
  setFeedbackByIndex: notWrapped,
  followups: [],
  setFollowups: notWrapped,
  isOpen: false,
  openChat: notWrapped,
  closeChat: notWrapped,
  isChatAvailable: false,
  setChatAvailable: notWrapped
})

interface ActiveEntry {
  id: string
  registration: ChatScreenRegistration
}

const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [active, setActive] = useState<ActiveEntry | null>(null)
  const [messages, setMessages] = useState<ChatpbChatMessage[]>([])
  const [rulesUpdatedIndexes, setRulesUpdatedIndexes] = useState<Set<number>>(
    () => new Set()
  )
  const [feedbackByIndex, setFeedbackByIndex] = useState<
    Map<number, ChatFeedbackRating>
  >(() => new Map())
  const [followups, setFollowups] = useState<ChatFollowupKind[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isChatAvailable, setChatAvailable] = useState(false)

  const openChat = useCallback(() => setIsOpen(true), [])
  const closeChat = useCallback(() => setIsOpen(false), [])

  const registerContext = useCallback(
    (id: string, registration: ChatScreenRegistration) => {
      setActive({ id, registration })
    },
    []
  )

  // Clear by registration identity, not tab: a late cleanup from a screen that
  // shared the same tab must not wipe a newer screen's active registration.
  const unregisterContext = useCallback((id: string) => {
    setActive((current) => (current?.id === id ? null : current))
  }, [])

  const value = useMemo(
    () => ({
      activeContext: active?.registration ?? null,
      registerContext,
      unregisterContext,
      messages,
      setMessages,
      rulesUpdatedIndexes,
      setRulesUpdatedIndexes,
      feedbackByIndex,
      setFeedbackByIndex,
      followups,
      setFollowups,
      isOpen,
      openChat,
      closeChat,
      isChatAvailable,
      setChatAvailable
    }),
    [
      active,
      registerContext,
      unregisterContext,
      messages,
      rulesUpdatedIndexes,
      feedbackByIndex,
      followups,
      isOpen,
      openChat,
      closeChat,
      isChatAvailable
    ]
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChatContext = () => useContext(ChatContext)

export default ChatProvider
